import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';


import useCountdownTimer from 'Hooks/useCountdownTimer';

/**
 * CountdownTimer component displays a countdown timer with days, hours, minutes, and seconds.
 * @param {number} props.value - The target timestamp for the countdown.
 * @param {boolean} [props.cardTimer=false] - Whether to apply card styling.
 * @param {string} [props.timerTitle=''] - Optional title to display above the timer.
 * @param {boolean} [props.daysText=false] - Whether to show "Days" text instead of "Left".
 * @param {boolean} [props.alwaysShowTimer=false] - Whether to always show the timer even if it has more than 1 day to go.
 */

export default function CountdownTimer({
  value,
  cardTimer,
  timerTitle,
  daysText,
  alwaysShowTimer,
}) {
  const { days, hours, minutes, seconds } = useCountdownTimer(value);
  // if (days > 1400) return false;

  const dealSecondsRemaining = (value - Date.now()) / 1000;
  const endingSoon = dealSecondsRemaining < 172800; // 48 hours
  const showTitle = timerTitle;

  if (!alwaysShowTimer && days <= 0 && hours <= 0 && minutes <= 0 && seconds <= 0) return false;

  if (!alwaysShowTimer && days >= 1) {
    return (
      <div className={classNames('CountdownTimer', { 'CountdownTimer--cardTimer': cardTimer }, { 'CountdownTimer--endingSoon': endingSoon })}>
        {showTitle &&
          <div className="CountdownTimer__title">
            {timerTitle}
          </div>
        }
        {!daysText &&
          <div className="CountdownTimer__clock CountdownTimer__clock--days">
            <FormattedMessage
              id="countdown-timer.days-left"
              defaultMessage="{days, plural, one {# Day} other {# Days}} Left"
              values={{ days }}
            />
          </div>
        }
        {daysText &&
          <div className="CountdownTimer__clock CountdownTimer__clock--days">
            <FormattedMessage
              id="countdown-timer.days"
              defaultMessage="{days} Days"
              values={{ days }}
            />
          </div>
        }
      </div>
    );
  }

  return (
    <div className={classNames('CountdownTimer', { 'CountdownTimer--cardTimer': cardTimer }, { 'CountdownTimer--endingSoon': endingSoon })}>
      {showTitle &&
        <div className="CountdownTimer__title">
          {timerTitle}
        </div>
      }
      <div className="CountdownTimer__clock">
        {days !== 0 &&
          <>
            <span>
              {days < 10 && <>0</>}
              {days}
              D
            </span>

            <span>
              :
            </span>
          </>
        }
        <span>
          {hours < 10 && <>0</>}
          {hours}
          H
        </span>

        <span>
          :
        </span>

        <span>
          {minutes < 10 && <>0</>}
          {minutes}
          M
        </span>

        <span>
          :
        </span>

        <span>
          {seconds < 10 && <>0</>}
          {seconds}
          S
        </span>
      </div>
    </div>
  );
}

CountdownTimer.propTypes = {
  value: PropTypes.number.isRequired,
  cardTimer: PropTypes.bool,
  timerTitle: PropTypes.string,
  daysText: PropTypes.bool,
  alwaysShowTimer: PropTypes.bool,
};

CountdownTimer.defaultTypes = {
  cardTimer: false,
  timerTitle: '',
  daysText: false,
  alwaysShowTimer: false,
};
